import React from 'react';
import { Search } from 'lucide-react';
import { Input } from '../../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { getUserAvatarUrl } from '../../../utils/api';
import { formatChatTime } from '../../../hooks/useCrmChat';

function getInitials(name) {
  return (name || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function EmployeeRow({ employee, isActive, onSelect }) {
  const preview = employee.last_message_preview;
  const time = formatChatTime(employee.last_message_at);
  const unread = employee.unread_count || 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(employee)}
      className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/60 border-b border-border/50 ${
        isActive ? 'bg-muted/80' : unread > 0 ? 'bg-muted/30' : ''
      }`}
    >
      <div className="relative shrink-0">
        <Avatar className="h-11 w-11">
          {employee.avatar ? (
            <AvatarImage src={getUserAvatarUrl(employee.avatar)} alt={employee.name} />
          ) : null}
          <AvatarFallback className="bg-muted text-foreground text-sm">
            {getInitials(employee.name)}
          </AvatarFallback>
        </Avatar>
        <span
          className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background ${
            employee.is_online ? 'bg-emerald-500' : 'bg-muted-foreground/40'
          }`}
          aria-hidden
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-foreground truncate">{employee.name}</span>
          {time && (
            <span className="text-[10px] text-muted-foreground shrink-0">{time}</span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {employee.designation || employee.role_name || 'CRM Employee'}
        </div>
        {preview && (
          <div className="text-xs text-muted-foreground truncate mt-0.5">{preview}</div>
        )}
      </div>
      {unread > 0 && (
        <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold flex items-center justify-center">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  );
}

export default function CrmChatEmployeeList({
  employees,
  loading,
  searchQuery,
  onSearchChange,
  activeContact,
  onSelect,
}) {
  return (
    <div className="flex flex-col h-full bg-card border-r border-border">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search name, ID, role..."
            className="pl-8 h-9 bg-muted border-border text-sm"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && employees.length === 0 ? (
          <div className="px-3 py-8 text-sm text-muted-foreground text-center">Loading employees...</div>
        ) : employees.length === 0 ? (
          <div className="px-3 py-8 text-sm text-muted-foreground text-center">No CRM employees found</div>
        ) : (
          employees.map((emp) => (
            <EmployeeRow
              key={emp.user_id}
              employee={emp}
              isActive={activeContact?.user_id === emp.user_id}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}
